import { MS, SECONDS } from "../time.ts";
import { isRejected } from "../types/is.ts";
import { sleep } from "./sleep.ts";

// Time throttling buckets for throttledAllSettled
const BUCKET_WINDOW_SIZE_MS = 25 * MS;
const BUCKET_SCALE_FACTOR = (1 * SECONDS) / BUCKET_WINDOW_SIZE_MS;

export type ThrottleOptions = {
  /** If passed, will log each error individually */
  logger?: { error: (data: object, message: string) => void };
  /** Maximum number of tasks to run concurrently */
  maxConcurrency?: number;
  /** Maximum number of tasks to run per second */
  maxPerSecond?: number;
};

/**
 * Similar to Promise.allSettled() but throttles execution to a maximum number of parallel operations.
 */
export const throttledAllSettled = async <T>(
  tasks: (() => Promise<T>)[],
  options?: ThrottleOptions
): Promise<PromiseSettledResult<Awaited<T>>[]> => {
  const maxConcurrency = options?.maxConcurrency ?? Infinity;
  const maxPerSecond = options?.maxPerSecond ?? Infinity;

  // Short-circuit if there is no throttling taking place
  if (tasks.length <= Math.min(maxConcurrency, maxPerSecond)) {
    const promises = tasks.map((task) => task());
    return Promise.allSettled(promises);
  }
  const results: PromiseSettledResult<Awaited<T>>[] = new Array(
    tasks.length
  ).fill(null);
  let index = 0;
  let requestTimerStart = Date.now();
  let requestsThisBucket = 0;

  // Copy collection so we don't alter it during operation
  const queue = [...tasks];
  const workers = Array(Math.min(maxConcurrency, tasks.length)).fill({});

  const loop = async (): Promise<void> => {
    // remember current `index` because it could be mutated by another worker by the time an `await` expression completes inside the `loop` function
    const currentIdx = index;
    index++;
    if (currentIdx < queue.length) {
      // Ensure rate limiting, done on BUCKET_WINDOW_SIZE intervals
      if (requestsThisBucket * BUCKET_SCALE_FACTOR >= maxPerSecond) {
        const timeToWait =
          BUCKET_WINDOW_SIZE_MS - (Date.now() - requestTimerStart);
        if (timeToWait > 0) await sleep(timeToWait);
        // Reset request counter
        requestTimerStart = Date.now();
        requestsThisBucket = 0;
      }

      try {
        const next = queue[currentIdx];
        requestsThisBucket++;
        const result = await next();
        results[currentIdx] = { status: "fulfilled", value: result };
      } catch (error: any) {
        options?.logger?.error(
          {
            ...error,
            index: currentIdx,
            error,
            stack: new Error().stack,
          },
          "Error during task execution"
        );
        results[currentIdx] = { status: "rejected", reason: error };
      }
      await loop();
    }
  };
  return Promise.allSettled(workers.map(loop)).then(() => results);
};

/**
 * Similar to Promise.all() but throttles execution to a maximum number of parallel operations
 */
export const throttledAll = async <T>(
  tasks: (() => Promise<T>)[],
  options?: { maxConcurrency?: number }
): Promise<T[]> => {
  const settled = await throttledAllSettled(tasks, options);
  const reject = settled.find(isRejected);
  if (reject) throw reject.reason;
  return settled.map((s) => (s as PromiseFulfilledResult<T>).value);
};
